import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router'
import { commandCenterApi } from '../services/api'
import { Button, PageHeader, PageLayout, StatePanel, StatusBadge, SummaryStrip } from './common'
import { Plus } from 'lucide-react'
import styles from './CommandCenterDashboard.module.css'

function GroupList({ groups, emptyTitle, footer }) {
  if (!groups.length) {
    return <StatePanel compact state="empty" title={emptyTitle} />
  }

  return (
    <div className={styles.list}>
      {groups.map((group) => (
        <Link key={group.id} to={`/command-center/post-groups/${group.id}`} className={styles.listItem}>
          <span className={styles.listItemMain}>
            <strong>{group.label || 'Untitled distributed post'}</strong>
            <StatusBadge auto>{group.approval_status}</StatusBadge>
          </span>
          <span className={styles.listItemMeta}>
            {group.post_count} organization{group.post_count === 1 ? '' : 's'}
            {(group.organizations || []).length ? ` · ${group.organizations.map((organization) => organization.name).join(', ')}` : ''}
          </span>
        </Link>
      ))}
      {footer}
    </div>
  )
}

function CommandCenterDashboard() {
  const navigate = useNavigate()
  const pendingQuery = useQuery({
    queryKey: ['cc-post-groups', 'pending'],
    queryFn: () => commandCenterApi.listPostGroups({ status: 'pending_approval', per_page: 5 }),
  })
  const recentQuery = useQuery({
    queryKey: ['cc-post-groups', 'recent'],
    queryFn: () => commandCenterApi.listPostGroups({ per_page: 5 }),
  })

  const pendingGroups = pendingQuery.data?.post_groups || []
  const recentGroups = recentQuery.data?.post_groups || []
  const pendingTotal = pendingQuery.data?.total || 0
  const recentTotal = recentQuery.data?.total || 0
  const hasError = pendingQuery.isError || recentQuery.isError

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Command Center"
        subtitle="Review cross-organization work and move approved content into Manage posts."
        primaryAction={(
          <Button onClick={() => navigate('/command-center/posts/new')}>
            <Plus size={16} aria-hidden="true" />
            Create post
          </Button>
        )}
      />

      <SummaryStrip
        label="Command Center work summary"
        items={[
          { key: 'pending', label: 'Pending approval', value: pendingTotal, description: `${pendingTotal} distributed posts awaiting review` },
          { key: 'recent', label: 'Distributed posts', value: recentTotal, description: `${recentTotal} distributed posts in the current result` },
        ]}
      />

      {hasError ? (
        <StatePanel
          state="error"
          title="Command Center data could not load"
          description="Retry both work queues without leaving this page."
          onRetry={() => {
            pendingQuery.refetch()
            recentQuery.refetch()
          }}
        />
      ) : (
        <div className={styles.grid}>
          <section className={styles.section} aria-labelledby="pending-groups-title">
            <div className={styles.sectionHeader}>
              <h2 id="pending-groups-title">Pending approvals</h2>
              <Link to="/command-center/approvals">Open approval queue</Link>
            </div>
            {pendingQuery.isLoading ? (
              <StatePanel compact state="loading" title="Loading pending approvals" />
            ) : (
              <GroupList
                groups={pendingGroups}
                emptyTitle="No pending approvals"
                footer={pendingTotal > 5 ? <Link to="/command-center/approvals" className={styles.footerLink}>View all ({pendingTotal})</Link> : null}
              />
            )}
          </section>

          <section className={styles.section} aria-labelledby="recent-groups-title">
            <div className={styles.sectionHeader}>
              <h2 id="recent-groups-title">Recent distributed posts</h2>
              <Link to="/command-center/posts">Open distributed posts</Link>
            </div>
            {recentQuery.isLoading ? (
              <StatePanel compact state="loading" title="Loading recent distributed posts" />
            ) : (
              <GroupList
                groups={recentGroups}
                emptyTitle="No distributed posts yet"
                footer={recentTotal > 5 ? <Link to="/command-center/posts" className={styles.footerLink}>View all ({recentTotal})</Link> : null}
              />
            )}
          </section>
        </div>
      )}
    </PageLayout>
  )
}

export default CommandCenterDashboard
